import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Avatar from '../../components/ui/Avatar';
import StatusPill from '../../components/ui/StatusPill';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import Modal from '../../components/ui/Modal';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconBuilding,
  IconTractor,
  IconUsers,
  IconTool,
  IconPencil,
  IconCheck,
  IconArrowRight,
} from '@tabler/icons-react';

export default function SiteDetail() {
  const { t } = useTranslation();
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { orgId, role } = useAuth();

  const [site, setSite] = useState(null);
  const [machines, setMachines] = useState([]);
  const [staff, setStaff] = useState([]);
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showEdit, setShowEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editName, setEditName] = useState('');
  const [editAddress, setEditAddress] = useState('');

  const canEdit = role === 'org_admin' || role === 'manager';

  useEffect(() => {
    const load = async () => {
      if (!orgId || !siteId) return;
      try {
        const [siteSnap, machinesSnap, usersSnap, defectsSnap] = await Promise.all([
          getDoc(doc(db, 'organisations', orgId, 'sites', siteId)),
          getDocs(collection(db, 'organisations', orgId, 'machines')),
          getDocs(collection(db, 'organisations', orgId, 'users')),
          getDocs(collection(db, 'organisations', orgId, 'defects')),
        ]);

        if (siteSnap.exists()) {
          const d = { id: siteSnap.id, ...siteSnap.data() };
          setSite(d);
          setEditName(d.name || '');
          setEditAddress(d.address || '');
        }

        // Everything on this page is scoped to the site by the same siteId the
        // rest of the app filters on.
        setMachines(machinesSnap.docs.map(m => ({ id: m.id, ...m.data() })).filter(m => m.siteId === siteId));
        setStaff(usersSnap.docs.map(u => ({ id: u.id, ...u.data() })).filter(u => u.siteId === siteId));
        setDefects(
          defectsSnap.docs
            .map(x => ({ id: x.id, ...x.data() }))
            .filter(x => x.siteId === siteId && x.status !== 'resolved')
        );
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, siteId]);

  const handleSave = async () => {
    if (!editName.trim()) { toast.error(t('set.err_site_name')); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'organisations', orgId, 'sites', siteId), {
        name: editName.trim(),
        address: editAddress.trim() || null,
        updatedAt: serverTimestamp(),
      });
      setSite(prev => ({ ...prev, name: editName.trim(), address: editAddress.trim() || null }));
      setShowEdit(false);
      toast.success(t('site.updated'));
    } catch (err) {
      console.error(err);
      toast.error(t('site.err_update'));
    }
    setSaving(false);
  };

  const roleLabel = (r) =>
    r === 'org_admin' ? t('role.admin') : r === 'manager' ? t('role.manager') : t('role.operator');

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <LoadingSkeleton rows={5} card />
      </div>
    </Layout>
  );

  if (!site) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div
          onClick={() => navigate('/settings/sites')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}
        >
          <IconArrowLeft size={15} stroke={1.5} /> {t('dashboard.sites')}
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>{t('site.not_found')}</div>
      </div>
    </Layout>
  );

  const openDefects = defects.length;

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 720, margin: '0 auto' }}>

        {/* Back */}
        <div
          onClick={() => navigate('/settings/sites')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}
        >
          <IconArrowLeft size={15} stroke={1.5} /> {t('dashboard.sites')}
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-lg)', background: 'var(--color-brand-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconBuilding size={22} color="var(--color-brand)" stroke={1.5} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 2 }}>{site.name}</h1>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                {site.address || t('site.no_address')}
              </div>
            </div>
          </div>
          {canEdit && (
            <Button
              variant="secondary"
              size="sm"
              icon={<IconPencil size={13} stroke={1.5} />}
              onClick={() => setShowEdit(true)}
            >
              {t('common.edit')}
            </Button>
          )}
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: t('machines.title'), value: machines.length, colour: 'var(--color-text-primary)' },
            { label: t('nav.staff'), value: staff.length, colour: 'var(--color-text-primary)' },
            { label: t('dashboard.open_defects'), value: openDefects, colour: openDefects > 0 ? 'var(--color-danger)' : 'var(--color-success)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 24, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Machines */}
        <Card style={{ marginBottom: 16 }} padding="0">
          <div style={{ fontSize: 14, fontWeight: 400, padding: '14px 18px', borderBottom: '0.5px solid var(--color-border-light)' }}>
            {t('machines.title')}
          </div>
          {machines.length === 0 ? (
            <EmptyState icon={<IconTractor size={28} stroke={1.5} />} title={t('site.no_machines')} />
          ) : machines.map((m, i) => (
            <div
              key={m.id}
              onClick={() => navigate('/machines/' + m.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', cursor: 'pointer',
                borderBottom: i < machines.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
              }}
            >
              <IconTractor size={16} color="var(--color-text-tertiary)" stroke={1.5} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>
                  {m.makeLabel} {m.modelLabel}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                  {m.registration || m.serialNumber || '—'}
                </div>
              </div>
              <StatusPill status={m.isBlocked ? 'blocked' : 'active'} />
            </div>
          ))}
        </Card>

        {/* Staff */}
        <Card style={{ marginBottom: 16 }} padding="0">
          <div style={{ fontSize: 14, fontWeight: 400, padding: '14px 18px', borderBottom: '0.5px solid var(--color-border-light)' }}>
            {t('nav.staff')}
          </div>
          {staff.length === 0 ? (
            <EmptyState icon={<IconUsers size={28} stroke={1.5} />} title={t('site.no_staff')} />
          ) : staff.map((u, i) => (
            <div
              key={u.id}
              onClick={() => navigate('/staff/' + u.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', cursor: 'pointer',
                borderBottom: i < staff.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
              }}
            >
              <Avatar name={u.name} photoURL={u.photoURL} size="sm" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{u.name}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{roleLabel(u.role)}</div>
              </div>
              <IconArrowRight size={14} color="var(--color-text-tertiary)" stroke={1.5} />
            </div>
          ))}
        </Card>

        {/* Open defects */}
        <Card padding="0">
          <div style={{ fontSize: 14, fontWeight: 400, padding: '14px 18px', borderBottom: '0.5px solid var(--color-border-light)' }}>
            {t('dashboard.open_defects')}
          </div>
          {defects.length === 0 ? (
            <EmptyState icon={<IconTool size={28} stroke={1.5} />} title={t('site.no_defects')} />
          ) : defects.map((d, i) => (
            <div
              key={d.id}
              onClick={() => navigate('/defects/' + d.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', cursor: 'pointer',
                borderBottom: i < defects.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                background: d.severity === 'critical' ? 'var(--color-danger-light)' : 'transparent',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: d.severity === 'critical' ? 'var(--color-danger)' : 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.description || t('defects.no_description')}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{d.machineName || '—'}</div>
              </div>
              <StatusPill status={d.severity || 'minor'} />
            </div>
          ))}
        </Card>
      </div>

      {/* Edit */}
      <Modal
        open={showEdit}
        onClose={() => setShowEdit(false)}
        title={t('site.edit')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowEdit(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving}
              loading={saving}
              icon={<IconCheck size={14} stroke={1.5} />}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              {t('set.site_name')} <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <input
              type="text"
              value={editName}
              onChange={e => setEditName(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              {t('set.address')}
            </label>
            <input
              type="text"
              value={editAddress}
              onChange={e => setEditAddress(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
